import moment, { Moment } from 'moment';
import { BankType, BrandType, NegotiationOption, SettingNameType, useGetSettingQuery } from '../generated/graphql';

/**
 * Hook que determina el rango de fechas válido para el picker de fecha de pago
 * en el formulario de resumen de negociación (`NonPaymentReasonForm`).
 *
 * **Prioridad del `maxPaymentDate`:**
 * 1. `FinalAssignmentDate.maxValidPaymentDate` del setting global (configurado por banco y marca).
 * 2. `option.settings.maxPaymentDate` enviado por el backend en las opciones de negociación
 *    (típicamente 3 días hábiles).
 * 3. Fin del mes en curso como fallback final.
 *
 * **Lógica de búsqueda en `FinalAssignmentDate`:**
 * - Para la marca `TRANQUIAPP` se busca por `bank` **y** `brand` (puede haber entradas
 *   de distintos bancos bajo la misma marca).
 * - Para otras marcas (portafolios como JCAP, BETA, PRA, etc.) se busca solo por `brand`,
 *   porque el banco no es discriminante en esos casos.
 *
 * **Impacto:** Visible tanto para asesores como para usuarios directos en flujos de
 * negociación con `extraConfirmationInfo = NewPaymentDate` o `PrePaymentDateAndInstallmentDay`.
 * El cambio es permisivo (amplía el rango de fechas seleccionables respecto al valor anterior
 * de `option.settings.maxPaymentDate`); la validación de negocio final se aplica en el backend.
 *
 * @param bank - Banco de la obligación. Por defecto `BankType.Rappipay`.
 * @param brand - Marca del portal. Por defecto `BrandType.Tranquiapp`.
 * @param option - Opción de negociación activa. Se usa como fuente secundaria del `maxPaymentDate`.
 * @returns `{ minPaymentDate, maxPaymentDate }` — ambos calculados en cada render para
 *          evitar valores desactualizados si la app permanece abierta más de un día.
 */
export const useGetMaxPaymentDateBySetting = (
  bank = BankType.Rappipay,
  brand = BrandType.Tranquiapp,
  option?: NegotiationOption,
): { minPaymentDate: Moment, maxPaymentDate: Moment } => {
  // Calculadas dentro del hook para que siempre reflejen la fecha actual del render
  const minPaymentDate = moment().startOf('day');
  const endOfMonth = moment().endOf('month');

  const { data: finalAssignmentDateData } = useGetSettingQuery({
    variables: { name: SettingNameType.FinalAssignmentDate },
    fetchPolicy: 'cache-first',
  });

  // Prioridad 1: buscar maxValidPaymentDate en el setting FINAL_ASSIGNMENT_DATE
  const maxDateSettings = brand === BrandType.Tranquiapp
    // Para Tranquiapp filtramos por banco Y marca (múltiples bancos bajo la misma marca)
    ? finalAssignmentDateData?.setting?.finalAssignmentDate
      ?.find((s) => bank === s.bank && brand === s.brand)?.maxValidPaymentDate
    // Para portafolios (JCAP, BETA, PRA, etc.) filtramos solo por marca
    : finalAssignmentDateData?.setting?.finalAssignmentDate
      ?.find((s) => brand === s.brand)?.maxValidPaymentDate;

  const maxDateSettingsMoment = moment(maxDateSettings);
  const validSettings = maxDateSettings
    && maxDateSettingsMoment.isValid()
    && maxDateSettingsMoment.isAfter(minPaymentDate);

  if (validSettings) {
    return { minPaymentDate, maxPaymentDate: maxDateSettingsMoment };
  }

  // Prioridad 2: usar el maxPaymentDate que envía el backend en la opción de negociación
  if (option?.settings?.maxPaymentDate) {
    return {
      minPaymentDate,
      maxPaymentDate: moment(option?.settings?.maxPaymentDate),
    };
  }

  // Fallback: fin del mes en curso
  return { minPaymentDate, maxPaymentDate: endOfMonth };
};
